/**
 * The trials-and-upgrades root's SHARED analytics vocabulary: the `screen_name`
 * tokens its thirteen surfaces report against, and the two context fragments
 * that recur across nearly every payload.
 *
 * WHY A SHARED FILE AND NOT ONE PER SURFACE. `screen_name` genuinely crosses
 * surfaces here, which it did not for the templates domain that ran this seam
 * first. `team-trial-upgrade-request-modal/atoms.ts` carries one in its open payload,
 * `TeamTrialUpgradeRequestButton` takes one as a prop, and the recap modal and
 * the clinician-trial-ended modal both supply theirs to those two. A token
 * declared per surface would be re-declared at every hand-off, and a duplicated
 * token is how one warehouse row becomes two — the same obstacle the nudge's
 * unions hit (`trial-upgrade-nudge-analytics.types.ts`, ruling 24).
 *
 * WHAT IS NOT HERE: any event NAME. All forty of this root's emits land as
 * `button_clicked` (54 occurrences), `modal_viewed` (20) or `modal_dismissed`
 * (3) — the app's cross-domain generic events, which no single domain owns and
 * therefore no domain may declare (D9 ruling 27: a name is globally unique and
 * the second declarer is a `duplicate-event-name` collision). Measured, not
 * assumed: `rg 'TrackingEventName\.' src/features/trialsAndUpgrades` returns
 * those three and nothing else. That is why `@heidi/commerce` gains no
 * `contributions/` directory and no `./contributions` door in this change —
 * there is nothing for it to declare. What the domain owns of those three
 * events is exactly this vocabulary and the payload shapes beside it.
 *
 * The emit is not here either. A package never imports a tracker (ruling 24):
 * the `Track…` seam types below and in the sibling files are what the app binds
 * in `src/models/analytics/features/`, and the app-side host is what adds the
 * event name and the destination routing.
 *
 * THE NUDGE KEEPS ITS OWN TOKENS. `trial_upgrade_nudge` and
 * `trial_upgrade_nudge_popover` stay declared in
 * `trial-upgrade-nudge-analytics.types.ts` rather than moving in here. They are
 * already declared once, their host already binds them, and re-minting them in
 * this object would create the second spelling this file exists to prevent.
 */
import type { SubscriptionTier } from '@heidi/schema/ml-scribe';

/**
 * Every `screen_name` this root reports against.
 *
 * Vocabulary, not mechanism: each spelling is keyed by a warehouse schema and
 * is already spelled by the app's `ScreenName` enum. The app-side binding
 * (`src/models/analytics/features/trialsAndUpgradesScreenNames.ts`) maps each
 * token to the enum member that declares it and its suite asserts the two agree
 * entry by entry, rather than letting a package token and an app enum drift into
 * two spellings of one row.
 */
export const TRIALS_AND_UPGRADES_SCREEN = {
  clinicianTrialEnded: 'clinician_trial_ended_modal',
  practiceTrialEnded: 'practice_trial_ended_modal',
  evidencePostUpgradeOnboarding: 'evidence_post_upgrade_onboarding_modal',
  evidencePostUpgradeWelcome: 'evidence_post_upgrade_welcome_popup',
  ninetyDayTrialEndDiscreet: 'ninety_day_trial_end_discreet_popup',
  ninetyDayTrialEndingSoonReminder: 'ninety_day_trial_ending_soon_reminder_popup',
  ninetyDayTrialRecap: 'ninety_day_trial_recap_modal',
  practiceInviteTeam: 'practice_invite_team_modal',
  practiceTrial: 'practice_trial_modal',
  teamTrialUpgradeRequest: 'team_trial_upgrade_request_modal',
  trialUpgradeRequestCallback: 'trial_upgrade_request_callback_modal',
} as const;

export type TrialsAndUpgradesScreenToken =
  (typeof TRIALS_AND_UPGRADES_SCREEN)[keyof typeof TRIALS_AND_UPGRADES_SCREEN];

/**
 * The viewer's current subscription tier, on the events that carry it.
 *
 * Optional because the tier query resolves asynchronously and every emitting
 * surface spreads it conditionally (`...(userTier !== undefined && { … })`)
 * rather than sending `undefined` — a shape the warehouse rows depend on and
 * this type preserves.
 */
export type TrialPlanContext = {
  current_plan?: SubscriptionTier;
};

/**
 * Team context, present only when the viewer belongs to a team.
 *
 * Both members are omitted together for a solo user, which is what
 * `getTeamContextAnalyticsProps` returns; keeping them optional rather than
 * nullable is what lets that helper's `{}` branch spread in unchanged.
 */
export type TrialTeamContext = {
  is_admin?: boolean;
  team_is_stripe_managed?: boolean;
};
